import type { IntlShape } from 'react-intl';
import type { FormFields } from '../../../models/newSupportCall';
import { F2_FORM_SUBJECTS } from '../constants';

export type RequiredFieldRule = {
    field: keyof FormFields;
    message: string;
};

export type TasAdditionalFieldKey =
    | 'partOrComponentName'
    | 'partNumber'
    | 'customerComment'
    | 'problemDescription'
    | 'diagnosticAndResults'
    | 'problemSteps'
    | 'failureFrequency'
    | 'failCodes'
    | 'repair'
    | 'observations';

export type TasAdditionalField = {
    field: TasAdditionalFieldKey;
    labelId: string;
    inputType: 'input' | 'textarea';
    required?: boolean;
    editableOnlyByVwco?: boolean;
};

const partOrComponentNameField: TasAdditionalField = {
    field: 'partOrComponentName',
    labelId: 'intl-msg:support.constants.field.partOrComponentName',
    inputType: 'input',
};

const partNumberField: TasAdditionalField = {
    field: 'partNumber',
    labelId: 'intl-msg:support.constants.field.partNumber',
    inputType: 'input',
};

const customerCommentField: TasAdditionalField = {
    field: 'customerComment',
    labelId: 'intl-msg:support.tasAdditionalRequiredFields.customerComplaint',
    inputType: 'textarea',
};

const problemDescriptionField: TasAdditionalField = {
    field: 'problemDescription',
    labelId: 'intl-msg:support.constants.field.problemDescription',
    inputType: 'textarea',
    required: false,
};

const diagnosticAndResultsField: TasAdditionalField = {
    field: 'diagnosticAndResults',
    labelId: 'intl-msg:support.constants.field.diagnosticAndResults',
    inputType: 'textarea',
};

const problemStepsField: TasAdditionalField = {
    field: 'problemSteps',
    labelId: 'intl-msg:support.constants.field.problemSteps',
    inputType: 'textarea',
};

const failureFrequencyField: TasAdditionalField = {
    field: 'failureFrequency',
    labelId: 'intl-msg:support.constants.field.failureFrequency',
    inputType: 'textarea',
};

const failCodesField: TasAdditionalField = {
    field: 'failCodes',
    labelId: 'intl-msg:support.constants.field.relatedFaultCodes',
    inputType: 'textarea',
    required: false,
};

const repairField: TasAdditionalField = {
    field: 'repair',
    labelId: 'intl-msg:support.constants.field.repairSolution',
    inputType: 'textarea',
    required: false,
    editableOnlyByVwco: true,
};

const observationsField: TasAdditionalField = {
    field: 'observations',
    labelId: 'intl-msg:support.constants.field.observations',
    inputType: 'textarea',
    required: false,
};

const tasCommonFields: TasAdditionalField[] = [partOrComponentNameField, partNumberField];

const getTasSubjectFields = (subject: string): TasAdditionalField[] => {
    if (F2_FORM_SUBJECTS.includes(subject)) {
        return [...tasCommonFields, customerCommentField, problemStepsField, diagnosticAndResultsField];
    }

    if (subject === 'CABIN') {
        return [...tasCommonFields, customerCommentField, problemDescriptionField, diagnosticAndResultsField];
    }

    if (subject === 'EXHAUST_SYSTEM_AND_AFTER_TREATMENT') {
        return [...tasCommonFields, customerCommentField, diagnosticAndResultsField];
    }

    if (subject === 'SOFTWARE_DIESEL_VEHICLE') {
        return [
            ...tasCommonFields,
            customerCommentField,
            failureFrequencyField,
            problemStepsField,
            diagnosticAndResultsField,
        ];
    }

    if (subject === 'ENGINE_REPLACEMENT_REQUEST' || subject === 'TAS_DIAGNOSIS_TOOLS') {
        return [...tasCommonFields, problemDescriptionField, diagnosticAndResultsField];
    }

    if (subject === 'SOFTWARE_IDLE_AUTO_SHUTDOWN') {
        return [...tasCommonFields, customerCommentField];
    }

    return [];
};

export const getTasAdditionalFields = (subject: string): TasAdditionalField[] => {
    const subjectFields = getTasSubjectFields(subject);

    return subjectFields.length > 0 ? [...subjectFields, failCodesField, repairField, observationsField] : [];
};

export const isTasAdditionalFieldRequired = (field: TasAdditionalField) => field.required !== false;

export const getTasAdditionalRequiredFields = (subject: string, intl: IntlShape): RequiredFieldRule[] =>
    getTasAdditionalFields(subject).filter(isTasAdditionalFieldRequired).map(field => ({
        field: field.field,
        message: intl.formatMessage(
            { id: 'intl-msg:support.tasAdditionalRequiredFields.fieldRequired' },
            { field: intl.formatMessage({ id: field.labelId }) },
        ),
    }));

export const isTasSupportSubject = (subject: string) => getTasAdditionalFields(subject).length > 0;
